import { useEffect } from 'react'

// Mark-as-paid — asks HOW the order was paid instead of a bare confirm().
// The chosen value is written to orders.paid_method, so a later accounting
// dashboard can split cash / Square / Zelle / comped without guessing.
//
// "free" records a comped order: the method is stored, the order keeps its
// amount, and nothing was collected.
//
// The Stripe row only appears when the order actually has a Stripe invoice —
// otherwise a card payment would end up recorded as cash.

const METHODS = [
  { key: 'cash',   label: 'Cash',       hint: 'Paid in person, cash' },
  { key: 'square', label: 'Square',     hint: 'Card terminal / Square app' },
  { key: 'zelle',  label: 'Zelle',      hint: 'Bank transfer' },
  { key: 'free',   label: 'Free order', hint: 'Comped — nothing collected' },
]

export default function MarkPaidModal({ open, order, busy = false, onClose, onConfirm }) {
  useEffect(() => {
    if (!open) return
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onEsc)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open || !order) return null

  const options = order.stripe_invoice_id
    ? [{ key: 'stripe', label: 'Stripe invoice', hint: 'Customer paid the invoice online' }, ...METHODS]
    : METHODS

  const amount = order.final_total_cents != null
    ? `$${(Number(order.final_total_cents) / 100).toFixed(2)}`
    : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-5 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="How was this order paid?"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-bricolage text-[18px] font-extrabold text-navy">
              Mark {order.order_number || 'order'} as paid
            </h2>
            <p className="mt-1 text-[12px] leading-relaxed text-ink-2">
              {[amount, order.weighed_lbs ? `${order.weighed_lbs} lbs` : null]
                .filter(Boolean)
                .join(' · ')}
              {amount || order.weighed_lbs ? ' — ' : ''}
              this closes the ticket for editing.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
            className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center text-[18px] font-bold text-ink-3 hover:text-navy disabled:opacity-40"
          >
            ×
          </button>
        </div>

        <p className="mt-4 text-[11px] font-bold uppercase tracking-wide text-ink-3">
          How did they pay?
        </p>

        <div className="mt-2 grid gap-2">
          {options.map((m) => (
            <button
              key={m.key}
              type="button"
              disabled={busy}
              onClick={() => onConfirm?.(m.key)}
              className="w-full rounded-xl border-[1.5px] border-line bg-white p-3 text-left transition hover:border-navy/40 disabled:opacity-50"
            >
              <div className="font-bricolage text-[15px] font-extrabold text-navy">
                {m.label}
              </div>
              <div className="mt-0.5 text-[11px] font-bold text-ink-2">{m.hint}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-[11px] text-ink-3">Saved on the order for accounting.</span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border-[1.5px] border-line px-4 py-2 text-[13px] font-bold text-navy disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
